import { create } from 'zustand';

/**
 * Ephemeral realtime SMS events from Socket.IO.
 * Not persisted — chat UIs subscribe and apply to local state.
 */
export const useSmsRealtimeStore = create((set, get) => ({
    /** Monotonic counter so React effects can react to each event */
    eventSeq: 0,
    /** Queued events (avoids dropping rapid SENT → DELIVERED updates) */
    eventQueue: [],
    /** Socket connection flag for reconnect refetch */
    connected: false,
    reconnectSeq: 0,

    publishEvent: (event) =>
        set((state) => ({
            eventQueue: [...state.eventQueue, event],
            eventSeq: state.eventSeq + 1,
        })),

    drainEvents: () => {
        const queued = get().eventQueue;
        if (!queued.length) {
            return [];
        }
        set({ eventQueue: [] });
        return queued;
    },

    setConnected: (connected) => {
        const wasConnected = get().connected;
        if (connected && !wasConnected) {
            set((state) => ({
                connected: true,
                reconnectSeq: state.reconnectSeq + 1,
            }));
        } else {
            set({ connected });
        }
    },
}));

/**
 * Normalize phone for comparing socket customer vs inbox keys.
 * Strips non-digits and a leading US country code 1.
 */
export function normalizePhone(value) {
    if (value == null || value === '') {
        return '';
    }
    let digits = String(value).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    return digits;
}

export function phonesMatch(a, b) {
    const na = normalizePhone(a);
    const nb = normalizePhone(b);
    return na !== '' && na === nb;
}

/**
 * Move/update a conversation row to the top of the inbox without a full refetch.
 */
export function bumpInboxConversation(users, {
    customer,
    lastMessage,
    customerFirstName,
    customerLastName,
}) {
    if (!Array.isArray(users)) {
        return users;
    }

    const idx = users.findIndex((u) => phonesMatch(u.customerNumber, customer));
    const names = [customerFirstName, customerLastName].filter(Boolean).join(' ').trim();
    const createdAt = new Date().toISOString();

    if (idx === -1) {
        return [
            {
                customerNumber: normalizePhone(customer) || customer,
                customerNames: names || customer,
                lastMessage,
                createdAt,
                unreadMessage: 0,
            },
            ...users,
        ];
    }

    const existing = users[idx];
    const updated = {
        ...existing,
        lastMessage,
        createdAt,
        unreadMessage: 0,
        customerNames: existing.customerNames || names || existing.customerNames,
    };
    return [updated, ...users.filter((_, i) => i !== idx)];
}

export function formatChatTimestamp(date = new Date()) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const formattedDate = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const formattedTime = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
    return `${formattedDate} ${formattedTime}`;
}

/**
 * Parse Socket.IO / notification-processor body into a structured chat event.
 */
export function parseSmsRealtimeEvent(data) {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const notificationType = String(data.notificationType ?? data.type ?? '').toUpperCase();
    if (!notificationType) {
        return null;
    }

    const createdRaw = data.createdAt ?? null;
    let createdAtMs = Date.now();
    if (createdRaw) {
        const parsed = Date.parse(createdRaw);
        if (!Number.isNaN(parsed)) {
            createdAtMs = parsed;
        }
    }

    return {
        notificationType,
        clinicId: data.clinicId != null ? String(data.clinicId) : null,
        messageId: data.messageId != null ? Number(data.messageId) : null,
        message: data.message ?? '',
        business: data.business != null ? String(data.business) : null,
        customer: data.customer != null ? String(data.customer) : null,
        messageType: data.messageType != null ? String(data.messageType).toUpperCase() : null,
        status: data.status != null ? String(data.status).toUpperCase() : null,
        customerFirstName: data.customerFirstName ?? null,
        customerLastName: data.customerLastName ?? null,
        createdAt: createdRaw,
        modifiedAt: formatChatTimestamp(createdAtMs),
        receivedAt: Date.now(),
    };
}
